import type { LucideIcon } from "lucide-react";
import * as React from "react";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import {
  AppWindow,
  Building2,
  CreditCard,
  Fingerprint,
  History,
  KeyRound,
  Laptop,
  Link2,
  Receipt,
  UserPlus,
} from "lucide-react";
import { motion } from "motion/react";

import { fetchGatewayIdentity } from "@/common/gateway-session";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/Button";
import { useIsAccountLoaded } from "@/hooks/useIsAccountLoaded";
import { useIsWondaCliPlansEnabled } from "@/hooks/useIsWondaCliPlansEnabled";
import { primeGatewayIdentity, useSessionAuthState } from "@/hooks/useSession";
import { useUser } from "@/hooks/useUser";
import { CliFooter } from "@/landing/components/CliFooter";
import { CliNav } from "@/landing/components/CliNav";

export const Route = createFileRoute("/_authenticated/(wonda)/_wonda/account")({
  beforeLoad: async ({ location }) => {
    if (location.searchStr.includes("skip_welcome")) {
      return;
    }
    const identity = await fetchGatewayIdentity();
    // Prime the identity query cache with this fresh read so AccountLayout's
    // useUser() resolves the SAME identity the guard just decided on. Without
    // it, a stale pre-onboarding cache (cliWelcomeCompleted false) survives its
    // staleTime and the layout effect bounces a just-onboarded user back to
    // /onboarding even though this guard already let them in.
    primeGatewayIdentity(identity);
    // Not provisioned yet — let the component render the setup spinner.
    if (!identity?.publicMetadata.accountId) {
      return;
    }
    if (!identity.publicMetadata.onboarded) {
      // Forward OAuth callback params so the connect-accounts step in the
      // welcome guide can show the toast + refresh its list rather than
      // losing them when we redirect away from /account.
      const params = new URLSearchParams(location.searchStr);
      const provider = parseOauthProvider(params.get("oauth_provider"));
      if (provider) {
        throw redirect({
          to: "/onboarding/guide",
          search: {
            step: "setup",
            oauth_provider: provider,
            oauth_error: params.get("oauth_error") ?? undefined,
          },
        });
      }
      throw redirect({ to: "/onboarding" });
    }
  },
  component: AccountLayout,
});

const TAB_DEFINITIONS: {
  to: string;
  label: string;
  icon: LucideIcon;
  requiresPlans?: boolean;
}[] = [
  {
    to: "/account/plan",
    label: "Your plan",
    icon: CreditCard,
    requiresPlans: true,
  },
  {
    to: "/account/invoices",
    label: "Invoices",
    icon: Receipt,
    requiresPlans: true,
  },
  {
    to: "/account/accounts",
    label: "Accounts",
    icon: Link2,
  },
  { to: "/account/setup", label: "Setup", icon: Laptop },
  { to: "/account/invite", label: "Invite", icon: UserPlus },
  { to: "/account/api-key", label: "API key", icon: KeyRound },
  { to: "/account/connected-apps", label: "Connected apps", icon: AppWindow },
  { to: "/account/generations", label: "Generations", icon: History },
  {
    to: "/account/organizations",
    label: "Organizations",
    icon: Building2,
  },
];

function AccountLayout() {
  const { isAccountLoaded, hasTimedOut, retry } = useIsAccountLoaded();
  const isPlansEnabled = useIsWondaCliPlansEnabled();
  const location = useLocation();
  const { user } = useUser();
  // "Signing out" = genuinely signed out (settled + no session), NOT merely
  // "identity not loaded yet". useUser().isSignedIn is identity-based and
  // flips false while userinfo is loading/erroring with a token still present;
  // use the token-based session state so a provisioning or degraded-userinfo
  // user sees the setup/retry UI, not "Signing you out...".
  const sessionAuth = useSessionAuthState();
  const isSigningOut = sessionAuth.isLoaded && !sessionAuth.isSignedIn;
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!isAccountLoaded) return;
    if (
      typeof window !== "undefined" &&
      window.location.search.includes("skip_welcome")
    ) {
      return;
    }
    if (!user?.publicMetadata?.cliWelcomeCompleted) {
      // Mirror the beforeLoad branch: forward OAuth params so the connect
      // step in the welcome guide can finish the round-trip.
      const params =
        typeof window !== "undefined"
          ? new URLSearchParams(window.location.search)
          : null;
      const provider = parseOauthProvider(params?.get("oauth_provider"));
      if (provider) {
        void navigate({
          to: "/onboarding/guide",
          search: {
            step: "setup",
            oauth_provider: provider,
            oauth_error: params?.get("oauth_error") ?? undefined,
          },
          replace: true,
        });
        return;
      }
      void navigate({ to: "/onboarding", replace: true });
    }
  }, [isAccountLoaded, user, navigate]);

  const tabs = TAB_DEFINITIONS.filter((tab) => {
    if (tab.requiresPlans && !isPlansEnabled) return false;
    return true;
  });

  if (!isAccountLoaded) {
    return (
      <AccountShell>
        <main className="flex flex-1 flex-col items-center justify-center gap-4">
          {isSigningOut ? (
            <>
              <Spinner variant="wonda" />
              <p className="text-lg font-medium text-(--cli-text-secondary)">
                Signing you out...
              </p>
            </>
          ) : hasTimedOut ? (
            <>
              <p className="text-lg font-medium text-(--cli-text-secondary)">
                Account setup is taking longer than expected
              </p>
              <p className="text-sm text-(--cli-text-secondary)">
                Please try again or refresh the page.
              </p>
              <Button variant="steward" size="default" onClick={retry}>
                Try again
              </Button>
            </>
          ) : (
            <>
              <Spinner variant="wonda" />
              <p className="text-lg font-medium text-(--cli-text-secondary)">
                Setting up your account...
              </p>
            </>
          )}
        </main>
      </AccountShell>
    );
  }

  return (
    <AccountShell>
      <main className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">
        <div className="flex flex-col gap-8 sm:flex-row">
          <nav className="flex w-full shrink-0 flex-row flex-wrap gap-1 sm:w-48 sm:flex-col">
            <p className="hidden px-3 pb-2 text-xs font-medium tracking-wide text-(--cli-text-muted) uppercase sm:block">
              Settings
            </p>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Link
                  key={tab.to}
                  to={tab.to}
                  className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                  activeProps={{
                    className: "text-(--cli-text) bg-(--hair)",
                  }}
                  inactiveProps={{
                    className: "text-(--cli-text-muted)",
                  }}
                >
                  <Icon className="size-4" strokeWidth={1.5} />
                  <span className="hidden sm:inline">{tab.label}</span>
                </Link>
              );
            })}
            {/* Credential self-service (passkeys, password) lives on Tank's
                own origin, so this is a full-page handoff via the server's
                /auth/security redirect, not a router Link. */}
            <a
              href="/auth/security"
              aria-label="Sign-in & security"
              className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-(--cli-text-muted) transition-colors"
            >
              <Fingerprint className="size-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">Sign-in & security</span>
            </a>
          </nav>

          <div className="max-w-lg min-w-0 flex-1">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <Outlet />
            </motion.div>
          </div>
        </div>

        <p className="mt-8 text-center text-sm text-(--cli-text-muted)">
          Any questions? Contact us at{" "}
          <a
            href="mailto:contact@wonda.sh"
            className="text-(--cli-text-secondary) underline transition-colors"
          >
            contact@wonda.sh
          </a>
        </p>
      </main>
    </AccountShell>
  );
}

// Shell shared by every account branch. The light
// ground comes from the global html/body base paint.
function AccountShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[100dvh] flex-col">
      <CliNav />
      {children}
      <CliFooter />
    </div>
  );
}

function parseOauthProvider(
  value: string | null | undefined,
): "instagram" | "tiktok" | "meta" | undefined {
  if (value === "instagram" || value === "tiktok" || value === "meta")
    return value;
  return undefined;
}
